import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';

/**
 * Fixtures for the DL-66233 epic (dl-58073, Testmo folder 93156, part 2 of 3) — the standard
 * "Description/Ready for export/Group/Last updated" columns plus the five custom-field-column
 * sort stories and the two non-sortable-column negatives.
 *
 * Additive only (CLAUDE.md §4 / `no-new-sprocs.md`'s sibling mock rule): every row here is
 * appended to `stylesData` on top of the existing 150+ generated + bulk-fixture rows, and the
 * new "Priority" custom field (715) is appended alongside the existing `styleCustomFieldDefinitions`
 * at the `/api/meta/styles/custom-fields` call site in `styles.ts` — nothing existing is edited.
 *
 * ## Custom-field sort support
 *
 * The mock's `sortRows` (`styles.ts`) had no support at all for `cf_<id>_<type>` sort keys before
 * this epic — an unrecognised property fell through to the default `''` sort value, so every style
 * tied and the "sort" was a no-op. `resolveStylesSortColumn` (`features/styles/api/getStyles.ts`)
 * proves the real frontend DOES send `cf_<id>_<type>` (see DL-66233's epic comment — this resolved
 * the 754488-vs-754674..754678 contradiction: custom fields ARE sortable), so the mock needed this
 * support to be a faithful (strict) handler for these five stories, per DL-66220's "strict handler"
 * premise that this epic inherits.
 *
 * `getCustomFieldSortValue` / `hasCustomFieldSortValue` below are consumed by `sortRows`'s new
 * `cf_`-prefixed branch. The chosen, documented contract (record of the "determine and record"
 * questions each custom-field story's ticket asks):
 *
 * - **Rows with no value for the sorted custom field always sort LAST, in both ASC and DESC.**
 *   (No case in the folder specifies this; "last regardless of direction" is the least surprising
 *   reading of "put unset values at the end of the list", and is what's implemented/asserted.)
 * - **Numeric** compares by parsed `Number`; a non-numeric stored value is never allowed to throw
 *   — it sorts as `+Infinity` (after every real number, alongside empty... but still distinguishable
 *   from empty because it never left the "has an entry" bucket). `0` is a real value, never treated
 *   as empty (a falsy-check bug this fixture is specifically seeded to catch).
 * - **Date** compares the raw ISO-ish stored string directly — chronological, not the `dd-MMM-yyyy`
 *   rendered string (see `ST-SORT-DATE-2018` below, which is chronologically first but would sort
 *   LAST under a naive rendered-string compare, because its rendered day is the highest of the set).
 * - **Text / single-select AllowedValue / Hierarchy (NestedAllowedValue)** all compare the row's
 *   own stored **label** (`entry.value.value` — exactly what the cell renders, per
 *   `getCustomFieldDisplayValue`), case-insensitively, trimmed, and with accents folded
 *   (`'Émile'`/`'Emile'` compare equal) — never the option id. For Hierarchy this means the
 *   **leaf label**, not the full ancestor path: `getCustomFieldDisplayValue` only ever surfaces the
 *   leaf's own `value` for a NestedAllowedValue field (there is no path concatenation anywhere in
 *   the real column/cell code), so sorting on that same string is the only contract consistent
 *   with what the column actually renders.
 */

let nextFixtureId = 96600;
const nextId = (): number => nextFixtureId++;

type Overrides = Partial<StyleRow> & { name: string; customId: string };

const baseSortFixtureRow = (overrides: Overrides): StyleRow => ({
  id: nextId(),
  description: null,
  state: 'published',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-02-01T09:00:00',
  readyForExport: false,
  ...overrides,
});

const cfEntry = (
  id: number,
  type: CustomFieldRowEntry['type'],
  value: string,
  valueId = 0
): CustomFieldRowEntry => ({ id, type, value: { id: valueId, value } });

// ============================================================================
// Custom field definition additions — appended alongside styleCustomFieldDefinitions
// ============================================================================

/** CF 715 "Priority" — Testmo 754676's own expected order (`Critical|High|Low|Medium|Normal`) is
 * ALPHABETICAL, not by severity — a deliberate id/label mismatch (ids assigned in the field's
 * natural severity order, labels alphabetised) so a sort-by-id regression is directly falsifiable
 * against a sort-by-label implementation. See DL-66241. */
export const PRIORITY_CUSTOM_FIELD_ID = 715;
export const sortEpicCustomFieldDefinitions = [
  {
    id: PRIORITY_CUSTOM_FIELD_ID,
    name: 'Priority',
    position: 15,
    type: 0, // AllowedValue (single-select)
    isMultipleAllowedValue: false,
    allowedValues: [
      { id: '71501', value: '71501', displayName: 'Normal' },
      { id: '71502', value: '71502', displayName: 'Medium' },
      { id: '71503', value: '71503', displayName: 'Low' },
      { id: '71504', value: '71504', displayName: 'High' },
      { id: '71505', value: '71505', displayName: 'Critical' },
    ],
  },
];

// ============================================================================
// Numeric custom field (708, "Target Weight (g)") — DL-66238 / Testmo 754678
// ============================================================================

const NUMERIC_CF_ID = 708;

const numericRow = (customId: string, name: string, value: string | null): StyleRow =>
  baseSortFixtureRow({
    name,
    customId,
    customFields: value == null ? [] : [cfEntry(NUMERIC_CF_ID, 'numeric', value)],
  });

/** Ascending, by style identity: 000 < 005 < 025 < 050 < 100 < 1000 < DIRTY (never empty). Testmo's
 * own 5-value core (`5|25|50|100|1000`) is a contiguous sub-sequence of this list. */
export const NUMERIC_SORT_CUSTOM_ID_ASC = [
  'ST-SORT-NUM-000',
  'ST-SORT-NUM-005',
  'ST-SORT-NUM-025',
  'ST-SORT-NUM-050',
  'ST-SORT-NUM-100',
  'ST-SORT-NUM-1000',
  'ST-SORT-NUM-DIRTY',
];
export const NUMERIC_SORT_EMPTY_CUSTOM_ID = 'ST-SORT-NUM-EMPTY';

const numericSortFixtureRows: StyleRow[] = [
  numericRow('ST-SORT-NUM-000', 'Sort Numeric CF Zero', '0'),
  numericRow('ST-SORT-NUM-005', 'Sort Numeric CF Five', '5'),
  numericRow('ST-SORT-NUM-025', 'Sort Numeric CF TwentyFive', '25'),
  numericRow('ST-SORT-NUM-050', 'Sort Numeric CF Fifty', '50'),
  numericRow('ST-SORT-NUM-100', 'Sort Numeric CF Hundred', '100'),
  numericRow('ST-SORT-NUM-1000', 'Sort Numeric CF Thousand', '1000'),
  // Dirty, non-numeric stored value — must never throw; sorts to the end (Infinity semantics).
  numericRow('ST-SORT-NUM-DIRTY', 'Sort Numeric CF Dirty', 'N/A'),
  numericRow(NUMERIC_SORT_EMPTY_CUSTOM_ID, 'Sort Numeric CF Empty', null),
];

// ============================================================================
// Date custom field (702, "Delivery Date") — DL-66239 / Testmo 754674
// ============================================================================

const DATE_CF_ID = 702;

const dateRow = (customId: string, name: string, value: string | null): StyleRow =>
  baseSortFixtureRow({
    name,
    customId,
    customFields: value == null ? [] : [cfEntry(DATE_CF_ID, 'date', value)],
  });

/** Chronologically ascending. `ST-SORT-DATE-2018` renders as "25-Nov-2018" (`dd-MMM-yyyy`) — its
 * rendered DAY component (25) is the highest of the set, so it sorts LAST under a naive
 * rendered-string compare despite being chronologically FIRST. That divergence is the falsifying
 * case DL-66239 asks for; Testmo's own 5-date core is the middle contiguous sub-sequence. The
 * malformed entry sorts after every valid date (never throws) and must render blank, not
 * "Invalid Date" (`formatDateTime` returns `''` on an unparsable string). */
export const DATE_SORT_CUSTOM_ID_ASC = [
  'ST-SORT-DATE-2018',
  'ST-SORT-DATE-2024',
  'ST-SORT-DATE-2025-06',
  'ST-SORT-DATE-2025-09',
  'ST-SORT-DATE-2026-01',
  'ST-SORT-DATE-2026-03',
  'ST-SORT-DATE-MALFORMED',
];
export const DATE_SORT_EMPTY_CUSTOM_ID = 'ST-SORT-DATE-EMPTY';
export const DATE_SORT_MALFORMED_CUSTOM_ID = 'ST-SORT-DATE-MALFORMED';

const dateSortFixtureRows: StyleRow[] = [
  dateRow('ST-SORT-DATE-2018', 'Sort Date CF 2018', '2018-11-25T00:00:00'),
  dateRow('ST-SORT-DATE-2024', 'Sort Date CF 2024', '2024-12-01T00:00:00'),
  dateRow('ST-SORT-DATE-2025-06', 'Sort Date CF 2025 Jun', '2025-06-01T00:00:00'),
  dateRow('ST-SORT-DATE-2025-09', 'Sort Date CF 2025 Sep', '2025-09-10T00:00:00'),
  dateRow('ST-SORT-DATE-2026-01', 'Sort Date CF 2026 Jan', '2026-01-15T00:00:00'),
  dateRow('ST-SORT-DATE-2026-03', 'Sort Date CF 2026 Mar', '2026-03-20T00:00:00'),
  dateRow('ST-SORT-DATE-MALFORMED', 'Sort Date CF Malformed', 'not-a-date'),
  dateRow(DATE_SORT_EMPTY_CUSTOM_ID, 'Sort Date CF Empty', null),
];

// ============================================================================
// Text custom field (701, "Fabric Notes") — DL-66240 / Testmo 754677
// ============================================================================

const TEXT_CF_ID = 701;

const textRow = (customId: string, name: string, value: string | null): StyleRow =>
  baseSortFixtureRow({
    name,
    customId,
    customFields: value == null ? [] : [cfEntry(TEXT_CF_ID, 'text', value)],
  });

export const TEXT_SORT_IDS = {
  caseLower: 'ST-SORT-TXT-CASE-A', // 'Apple'
  caseUpper: 'ST-SORT-TXT-CASE-Z', // 'zebra'
  wsTrimmed: 'ST-SORT-TXT-WS-2', // 'Yolanda'
  wsLeading: 'ST-SORT-TXT-WS-1', // ' Zebra' (leading space)
  accentFolded: 'ST-SORT-TXT-ACCENT-1', // 'Émile'
  accentPlain: 'ST-SORT-TXT-ACCENT-2', // 'Fmile'
  numLike10: 'ST-SORT-TXT-NUM-10',
  numLike100: 'ST-SORT-TXT-NUM-100',
  numLike9: 'ST-SORT-TXT-NUM-9',
  longA: 'ST-SORT-TXT-LONG-1',
  longB: 'ST-SORT-TXT-LONG-2',
  html: 'ST-SORT-TXT-HTML',
  empty: 'ST-SORT-TXT-EMPTY',
} as const;

const LONG_PREFIX = 'A'.repeat(60);

const textSortFixtureRows: StyleRow[] = [
  textRow(TEXT_SORT_IDS.caseLower, 'Sort Text CF Case Apple', 'Apple'),
  textRow(TEXT_SORT_IDS.caseUpper, 'Sort Text CF Case zebra', 'zebra'),
  textRow(TEXT_SORT_IDS.wsLeading, 'Sort Text CF Whitespace Leading', ' Zebra'),
  textRow(TEXT_SORT_IDS.wsTrimmed, 'Sort Text CF Whitespace Trimmed', 'Yolanda'),
  textRow(TEXT_SORT_IDS.accentFolded, 'Sort Text CF Accent Emile', 'Émile'),
  textRow(TEXT_SORT_IDS.accentPlain, 'Sort Text CF Accent Fmile', 'Fmile'),
  textRow(TEXT_SORT_IDS.numLike9, 'Sort Text CF Numeric-looking 9', '9'),
  textRow(TEXT_SORT_IDS.numLike10, 'Sort Text CF Numeric-looking 10', '10'),
  textRow(TEXT_SORT_IDS.numLike100, 'Sort Text CF Numeric-looking 100', '100'),
  textRow(TEXT_SORT_IDS.longA, 'Sort Text CF Long Prefix A', `${LONG_PREFIX}1`),
  textRow(TEXT_SORT_IDS.longB, 'Sort Text CF Long Prefix B', `${LONG_PREFIX}2`),
  textRow(TEXT_SORT_IDS.html, 'Sort Text CF HTML', '<b>bold</b> text'),
  textRow(TEXT_SORT_IDS.empty, 'Sort Text CF Empty', null),
];

// ============================================================================
// Priority custom field (715, single-select AllowedValue) — DL-66241 / Testmo 754676
// ============================================================================

const priorityRow = (
  customId: string,
  name: string,
  value: { id: number; value: string } | null
): StyleRow =>
  baseSortFixtureRow({
    name,
    customId,
    customFields:
      value == null
        ? []
        : [cfEntry(PRIORITY_CUSTOM_FIELD_ID, 'allowedvalue', value.value, value.id)],
  });

/** Testmo 754676's own expected order, verbatim — alphabetical, NOT severity order. Also proves
 * "sorted by label, not id": the definition's id order (71501..71505) is Normal/Medium/Low/High/
 * Critical, the REVERSE of this list. */
export const PRIORITY_SORT_CUSTOM_ID_ASC = [
  'ST-SORT-PRI-CRITICAL',
  'ST-SORT-PRI-HIGH',
  'ST-SORT-PRI-LOW',
  'ST-SORT-PRI-MEDIUM',
  'ST-SORT-PRI-NORMAL',
];
export const PRIORITY_SORT_EMPTY_CUSTOM_ID = 'ST-SORT-PRI-EMPTY';

const prioritySortFixtureRows: StyleRow[] = [
  priorityRow('ST-SORT-PRI-CRITICAL', 'Sort Priority CF Critical', {
    id: 71505,
    value: 'Critical',
  }),
  priorityRow('ST-SORT-PRI-HIGH', 'Sort Priority CF High', { id: 71504, value: 'High' }),
  priorityRow('ST-SORT-PRI-LOW', 'Sort Priority CF Low', { id: 71503, value: 'Low' }),
  priorityRow('ST-SORT-PRI-MEDIUM', 'Sort Priority CF Medium', { id: 71502, value: 'Medium' }),
  priorityRow('ST-SORT-PRI-NORMAL', 'Sort Priority CF Normal', { id: 71501, value: 'Normal' }),
  priorityRow(PRIORITY_SORT_EMPTY_CUSTOM_ID, 'Sort Priority CF Empty', null),
];

// ============================================================================
// Hierarchy custom field (706, "Print Placement", child of 705) — DL-66242 / Testmo 754675
// ============================================================================

const HIERARCHY_CF_ID = 706;

const hierarchyRow = (
  customId: string,
  name: string,
  value: { id: number; value: string } | null
): StyleRow =>
  baseSortFixtureRow({
    name,
    customId,
    customFields:
      value == null ? [] : [cfEntry(HIERARCHY_CF_ID, 'nestedallowedvalue', value.value, value.id)],
  });

/** Alphabetical by LEAF label (the contract this fixture file's header records — see there for
 * why leaf, not full path). Definition id order is Chest(7061)/Sleeve(7062)/Back(7063)/
 * Collar(7064) — different from this list, proving the sort uses the label, not the node id. */
export const HIERARCHY_SORT_CUSTOM_ID_ASC = [
  'ST-SORT-HIER-BACK',
  'ST-SORT-HIER-CHEST',
  'ST-SORT-HIER-COLLAR',
  'ST-SORT-HIER-SLEEVE',
];
export const HIERARCHY_SORT_EMPTY_CUSTOM_ID = 'ST-SORT-HIER-EMPTY';

const hierarchySortFixtureRows: StyleRow[] = [
  hierarchyRow('ST-SORT-HIER-CHEST', 'Sort Hierarchy CF Chest', { id: 7061, value: 'Chest' }),
  hierarchyRow('ST-SORT-HIER-SLEEVE', 'Sort Hierarchy CF Sleeve', { id: 7062, value: 'Sleeve' }),
  hierarchyRow('ST-SORT-HIER-BACK', 'Sort Hierarchy CF Back', { id: 7063, value: 'Back' }),
  hierarchyRow('ST-SORT-HIER-COLLAR', 'Sort Hierarchy CF Collar', { id: 7064, value: 'Collar' }),
  hierarchyRow(HIERARCHY_SORT_EMPTY_CUSTOM_ID, 'Sort Hierarchy CF Empty', null),
];

// ============================================================================
// Standard columns — Description (DL-66234), Group (DL-66236), Last updated (DL-66237)
// ============================================================================

// A leading '~' (code point 0x7E) sorts after every faker-generated real description (which are
// ordinary lowercased sentences), so under a DESCENDING sort these two rows are guaranteed to
// render at the very top regardless of the ~75 other populated descriptions competing for space —
// a real backend's collation may differ, but this mock's plain `<`/`>` compare on lowercased text
// makes '~' reliably extremal. See DESCRIPTION_TRUNCATION_IDS' consuming spec for why this matters
// (virtualization only ever renders the first handful of rows).
const DESC_PREFIX =
  '~~~ Sort description truncation probe with a shared sixty-plus character prefix ';

export const DESCRIPTION_TRUNCATION_IDS = {
  a: 'ST-SORT-DESC-A',
  b: 'ST-SORT-DESC-B',
} as const;

const descriptionSortFixtureRows: StyleRow[] = [
  baseSortFixtureRow({
    name: 'Sort Description Truncation A',
    customId: DESCRIPTION_TRUNCATION_IDS.a,
    description: `${DESC_PREFIX}— ending in ONE.`,
  }),
  baseSortFixtureRow({
    name: 'Sort Description Truncation B',
    customId: DESCRIPTION_TRUNCATION_IDS.b,
    description: `${DESC_PREFIX}— ending in TWO.`,
  }),
  baseSortFixtureRow({
    name: 'Sort Description Special Characters',
    customId: 'ST-SORT-DESC-SPECIAL',
    description: 'Line one.\nLine two — "quoted" & <tagged> text.',
  }),
];

export const GROUP_NONE_CUSTOM_ID = 'ST-SORT-GROUP-NONE';

const groupSortFixtureRows: StyleRow[] = [
  baseSortFixtureRow({
    name: 'Sort Group None',
    customId: GROUP_NONE_CUSTOM_ID,
    group: null,
  }),
];

export const LAST_UPDATED_IDS = {
  earliest: 'ST-SORT-LU-OLD',
  latest: 'ST-SORT-LU-NEW',
  sameDayEarly: 'ST-SORT-LU-DAY-EARLY',
  sameDayLate: 'ST-SORT-LU-DAY-LATE',
  malformed: 'ST-SORT-LU-MALFORMED',
} as const;

const lastUpdatedSortFixtureRows: StyleRow[] = [
  baseSortFixtureRow({
    name: 'Sort Last Updated Oldest',
    customId: LAST_UPDATED_IDS.earliest,
    lastUpdated: '2000-01-01T00:00:00',
  }),
  baseSortFixtureRow({
    name: 'Sort Last Updated Newest',
    customId: LAST_UPDATED_IDS.latest,
    lastUpdated: '2030-01-01T00:00:00',
  }),
  // Same calendar day, different times — proves the sort uses the full instant, not a
  // date-truncated value (a stand-in for the folder's cross-timezone-day-boundary concern, which
  // this mock's local-implicit ISO storage — no explicit UTC offset — cannot otherwise model).
  // Deliberately in 2001 (not 2025, where the 150 generated rows' `lastUpdated` values live) so
  // both rows stay extremal near the front of an ascending sort — a rendered/virtualized read
  // only ever sees the first handful of rows, so a same-day pair competing against ~150 real 2025
  // dates for a spot near the top would not reliably render.
  baseSortFixtureRow({
    name: 'Sort Last Updated Same Day Early',
    customId: LAST_UPDATED_IDS.sameDayEarly,
    lastUpdated: '2001-01-01T01:00:00',
  }),
  baseSortFixtureRow({
    name: 'Sort Last Updated Same Day Late',
    customId: LAST_UPDATED_IDS.sameDayLate,
    lastUpdated: '2001-01-01T23:00:00',
  }),
  // Malformed timestamp — `formatDateTime` returns '' (blank) on an unparsable string rather than
  // "Invalid Date"; the raw string never throws in a plain `<`/`>` compare either.
  baseSortFixtureRow({
    name: 'Sort Last Updated Malformed',
    customId: LAST_UPDATED_IDS.malformed,
    lastUpdated: 'not-a-timestamp',
  }),
];

// ============================================================================
// Non-sortable columns — Categories (DL-66243), Style colors (DL-66244)
// ============================================================================

export const STYLE_COLORS_IDS = {
  one: 'ST-SORT-COLORS-ONE',
  many: 'ST-SORT-COLORS-MANY',
  none: 'ST-SORT-COLORS-NONE',
} as const;

const styleColorsSortFixtureRows: StyleRow[] = [
  baseSortFixtureRow({
    name: 'Sort Style Colors One',
    customId: STYLE_COLORS_IDS.one,
    styleColors: [{ id: 1, name: 'Cobalt Blue', customId1: 'C1', customId2: 'C2' }],
  }),
  baseSortFixtureRow({
    name: 'Sort Style Colors Many',
    customId: STYLE_COLORS_IDS.many,
    styleColors: [
      { id: 2, name: 'Cobalt Blue', customId1: 'C1', customId2: 'C2' },
      { id: 3, name: 'Forest Green', customId1: 'C3', customId2: 'C4' },
      { id: 4, name: 'Sunset Orange', customId1: 'C5', customId2: 'C6' },
    ],
  }),
  baseSortFixtureRow({
    name: 'Sort Style Colors None',
    customId: STYLE_COLORS_IDS.none,
    styleColors: [],
  }),
];

// ============================================================================
// Multi-select AllowedValue custom field (704, "Fabric Blend") — DL-66257 / Testmo 754681. Real
// entries (`values[]`, the plural, multi-value shape — NOT `value`) so a genuinely non-empty
// multiselect row exists to click-and-observe against the single-select CF 715 positive control.
// ============================================================================

const MULTISELECT_CF_ID = 704;

const multiselectRow = (
  customId: string,
  name: string,
  values: Array<{ id: number; value: string }>
): StyleRow =>
  baseSortFixtureRow({
    name,
    customId,
    customFields: [{ id: MULTISELECT_CF_ID, type: 'allowedvalue', values }],
  });

export const MULTISELECT_SORT_IDS = {
  many: 'ST-SORT-MULTI-MANY',
  one: 'ST-SORT-MULTI-ONE',
  none: 'ST-SORT-MULTI-NONE',
} as const;

const multiselectSortFixtureRows: StyleRow[] = [
  multiselectRow('ST-SORT-MULTI-ZEBRA', 'Sort Multiselect CF Zebra', [
    { id: 7046, value: '7046' },
    { id: 7042, value: '7042' },
  ]),
  multiselectRow(MULTISELECT_SORT_IDS.one, 'Sort Multiselect CF One Value', [
    { id: 7041, value: '7041' },
  ]),
  multiselectRow(MULTISELECT_SORT_IDS.many, 'Sort Multiselect CF Apple', [
    { id: 7043, value: '7043' },
    { id: 7044, value: '7044' },
    { id: 7045, value: '7045' },
  ]),
  multiselectRow(MULTISELECT_SORT_IDS.none, 'Sort Multiselect CF Empty', []),
];

// ============================================================================
// Assembly + custom-field sort-value support consumed by styles.ts
// ============================================================================

export const buildStylesSortFixtureStyles = (): StyleRow[] => [
  ...numericSortFixtureRows,
  ...dateSortFixtureRows,
  ...textSortFixtureRows,
  ...prioritySortFixtureRows,
  ...hierarchySortFixtureRows,
  ...descriptionSortFixtureRows,
  ...groupSortFixtureRows,
  ...lastUpdatedSortFixtureRows,
  ...styleColorsSortFixtureRows,
  ...multiselectSortFixtureRows,
];

const CF_COLUMN_ID_PATTERN = /^cf_(\d+)_([a-z0-9-]+)$/i;

const parseCfColumnId = (property: string): { id: number; suffix: string } | null => {
  const match = CF_COLUMN_ID_PATTERN.exec(property);
  if (!match) return null;
  return { id: Number(match[1]), suffix: match[2].toLowerCase() };
};

/** Case-insensitive, trimmed, accent-folded — the recorded text/label comparison contract (see
 * file header). Plain ASCII output, so a normal `<`/`>` compare is a correct collation. */
const normalizeLabel = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/** Whether `row` carries a real (non-empty) value for the `cf_<id>_<suffix>` property — used by
 * `sortRows`'s custom-field branch to bucket "has a value" rows away from "no value" ones, which
 * always sort last regardless of direction (see file header). */
export const hasCustomFieldSortValue = (row: StyleRow, property: string): boolean => {
  const parsed = parseCfColumnId(property);
  if (!parsed) return false;
  const entry = row.customFields.find(cf => cf.id === parsed.id);
  const raw = entry?.value?.value;
  return raw != null && raw !== '';
};

/** The comparable sort key for a `cf_<id>_<suffix>` property on a row that `hasCustomFieldSortValue`
 * has already confirmed carries a value. Numeric never throws on dirty data (sorts as +Infinity);
 * date compares the raw ISO-ish string chronologically; every other type compares the normalized
 * label. */
export const getCustomFieldSortValue = (row: StyleRow, property: string): number | string => {
  const parsed = parseCfColumnId(property);
  if (!parsed) return '';
  const entry = row.customFields.find(cf => cf.id === parsed.id);
  const raw = entry?.value?.value ?? '';
  if (parsed.suffix === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
  }
  if (parsed.suffix === 'date') return raw;
  return normalizeLabel(raw);
};

/**
 * Custom-field (`cf_<id>_<type>`) sort — DL-66233. Rows with no value for the sorted field always
 * sort LAST, in both ASC and DESC (see this file's header for the full contract). Lives here
 * (rather than inline in `styles.ts`'s `sortRows`) purely to keep that file under the repo's
 * 800-line lint cap — same reasoning as every other `stylesXFixtureData.ts` split in this
 * directory. `styles.ts`'s `sortRows` calls this directly for any `cf_`-prefixed property; every
 * existing standard-column sort path there is untouched.
 */
export const sortCustomFieldRows = (
  rows: StyleRow[],
  property: string,
  order: string
): StyleRow[] => {
  const withValue = rows.filter(r => hasCustomFieldSortValue(r, property));
  const withoutValue = rows.filter(r => !hasCustomFieldSortValue(r, property));
  const sorted = [...withValue].sort((a, b) => {
    const av = getCustomFieldSortValue(a, property);
    const bv = getCustomFieldSortValue(b, property);
    if (av < bv) return -1;
    if (av > bv) return 1;
    return a.id - b.id; // stable tiebreak
  });
  const ordered = order === 'desc' ? sorted.reverse() : sorted;
  return [...ordered, ...withoutValue];
};
